/**
 * `password → TOTP → session cookie`, plus forced first enrolment. Task 30.
 *
 * **`UI-Design-Spec.md` specifies no login screen.** It has eighteen — S1 to S18 — and none
 * of them is this one, which is a gap in the design authority rather than a decision. So the
 * behaviour here comes from `AuthN-AuthZ-and-User-Management.md#operator-login`, which is
 * specific, and the appearance from `Visual-Design-Language.md` and the vendored components.
 * Raised in BUILD-LOG rather than resolved by writing a screen into the vault.
 *
 * ## Four steps, and the order is a safety property
 *
 * 1. **password** — `POST /auth/login`. Answers `{mfa_required, mfa_token, enrolment_required}`
 *    and deliberately **sets no session cookie**: a password alone must not authenticate.
 * 2. **enrol** — only when `enrolment_required`. `AuthN-AuthZ`: *"First run forces enrolment;
 *    MFA cannot be disabled while a live deployment exists."*
 * 3. **code** — `POST /auth/mfa`, six digits *or* a recovery code. One field, two shapes,
 *    because the server accepts both there (`MfaRequest.code`).
 * 4. **codes** — the ten recovery codes, shown once, after a successful enrolment.
 *
 * ## What this screen must not do
 *
 * **Not explain why a login failed.** `problems.py` answers a wrong password, an unknown
 * email, an expired challenge and a bad code with one code and one sentence, on purpose —
 * distinguishing them would answer "does this account exist?". This renders the server's
 * sentence and adds nothing to it. The temptation to be helpful here is the vulnerability.
 *
 * **Not keep the password.** It goes into `login()` and the field is cleared on success; it
 * is never held to "retry" a later step, because the later steps do not need it and a
 * password sitting in component state through a two-minute enrolment is a password in a heap
 * snapshot.
 *
 * **Not treat the recovery codes as dismissible chrome.** They are shown exactly once, the
 * server has already stored only their hashes, and there is no second chance. Continuing is
 * gated on an explicit acknowledgement.
 */

import { useState } from 'react';

import { api } from '../api/client';
import { messageOf, requestIdOf } from '../api/errors';
import { useAuth } from '../auth/AuthProvider';
import { Wordmark } from '../design/components/chrome/Wordmark.jsx';
import { Button } from '../design/components/controls/Button.jsx';
import { FormField } from '../design/components/controls/FormField.jsx';
import { CopyableId } from '../design/components/controls/CopyableId.jsx';
import { Banner } from '../design/components/messages/Banner.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';

type Step = 'password' | 'enrol' | 'code' | 'codes' | 'spent';

interface Enrolment {
  enrolmentToken: string;
  provisioningUri: string;
  secret: string;
}

export function LoginScreen(): React.JSX.Element {
  const { refresh } = useAuth();

  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  function fail(cause: unknown, fallback: string): void {
    setError({ message: messageOf(cause, fallback), requestId: requestIdOf(cause) });
  }

  async function run(work: () => Promise<void>, fallback: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (cause) {
      fail(cause, fallback);
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = () =>
    run(async () => {
      const answer = await api.auth.login({ requestBody: { email, password } });
      setMfaToken(answer.mfa_token);
      // Cleared here rather than on unmount: from this point nothing needs it, and the
      // remaining steps can take a while.
      setPassword('');
      if (answer.enrolment_required) {
        const started = await api.auth.enrolMfa({ requestBody: { mfa_token: answer.mfa_token } });
        setEnrolment({
          enrolmentToken: started.enrolment_token,
          provisioningUri: started.provisioning_uri,
          secret: started.secret,
        });
        setStep('enrol');
      } else {
        setStep('code');
      }
    }, 'Sign-in failed.');

  const submitEnrolment = () =>
    run(async () => {
      if (!enrolment) throw new Error('no enrolment in progress');
      const confirmed = await api.auth.confirmEnrolment({
        requestBody: { enrolment_token: enrolment.enrolmentToken, code },
      });
      setRecoveryCodes(confirmed.recovery_codes);
      setCode('');
      setEnrolment(null);
      setStep('codes');
    }, 'That code was not accepted.');

  const submitCode = () =>
    run(async () => {
      const account = await api.auth.completeMfa({ requestBody: { mfa_token: mfaToken, code } });
      setCode('');
      // `recovery_codes_remaining` is set **only when a recovery code was just spent** — the
      // server puts it there specifically so the UI can say how many are left. Signing in
      // this way silently would let an operator burn all ten without ever being told, and
      // the tenth failure is discovered at the worst possible moment.
      if (account.recovery_codes_remaining != null) {
        setRemaining(account.recovery_codes_remaining);
        setStep('spent');
        return;
      }
      await refresh();
    }, 'That code was not accepted.');

  // Enrolment confirms *and* signs in — the server issues the session in the same call — so
  // finishing here is `refresh()`, not another round trip.
  const finish = () => run(() => refresh(), 'Could not read the new session.');

  return (
    <main className="jmt-login">
      <div className={`jmt-login__panel${step === 'codes' ? ' jmt-login__panel--wide' : ''}`}>
        {/* The stacked lockup rather than the horizontal one: this is the only surface in
            the product where the brand is the largest thing on screen, and a 380px panel is
            the narrow case the stacked variant exists for. `h1` still, for the document
            outline — the visible text is inside the SVG's aria-label. */}
        <h1 className="jmt-login__title">
          <Wordmark variant="stacked" style={{ width: 148, color: 'var(--accent)' }} />
        </h1>
        <p className="jmt-login__subtitle">{STEP_SUBTITLE[step]}</p>

        {error ? (
          <div style={{ marginBottom: 'var(--space-16)' }}>
            <ErrorPanel
              message={error.message}
              detail={error.requestId ? `request_id ${error.requestId}` : undefined}
            />
          </div>
        ) : null}

        <form
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            if (busy) return;
            if (step === 'password') void submitPassword();
            else if (step === 'enrol') void submitEnrolment();
            else if (step === 'code') void submitCode();
            else void finish();
          }}
          className="jmt-login__form"
        >
          {step === 'password' ? (
            <>
              <FormField
                label="Email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                help="The operator account this platform was set up with."
                id="email"
              />
              <FormField
                label="Password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                help="A password alone does not sign you in — a code from your authenticator is always required."
                id="password"
              />
            </>
          ) : null}

          {step === 'enrol' && enrolment ? (
            <>
              <Banner
                tone="neutral"
                title="Set up your authenticator now"
                detail="This account has no second factor yet. Add the key below to an authenticator app, then enter the six digits it shows. Nothing is saved until a code verifies, so a mistake here costs you nothing."
              />
              <div>
                <div className="jmt-login__field-label">Setup key</div>
                {/* The secret, not a QR code. Rendering a QR needs a library, and typing the
                    key into an authenticator is a one-time cost — where a QR library would be
                    a permanent dependency handling a value that is a credential. */}
                <CopyableId value={enrolment.secret} />
              </div>
              <FormField
                label="Code from your authenticator"
                value={code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                help="Six digits. It changes every thirty seconds."
                numeric
                id="enrol-code"
              />
            </>
          ) : null}

          {step === 'code' ? (
            <FormField
              label="Authentication code"
              value={code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
              help="Six digits from your authenticator, or one of your recovery codes."
              id="code"
            />
          ) : null}

          {step === 'spent' ? (
            <Banner
              tone={remaining != null && remaining <= 2 ? 'loss' : 'neutral'}
              title={
                remaining === 1
                  ? 'You have one recovery code left'
                  : `You signed in with a recovery code — ${remaining ?? 0} left`
              }
              detail={
                remaining === 0
                  ? 'That was the last one. Re-enrol your authenticator from Settings to get a new set, or you will have no way back in if you lose the device.'
                  : 'Recovery codes are single-use. Re-enrol your authenticator from Settings when you want a fresh set of ten.'
              }
            />
          ) : null}

          {step === 'codes' ? (
            <>
              <Banner
                tone="loss"
                title="Write these down before continuing"
                detail="Ten single-use recovery codes. They are shown once and are stored only as hashes — nobody can show them to you again. They are the only way in if you lose your authenticator, and multi-factor authentication cannot be turned off while anything is deployed live."
              />
              <ul className="jmt-codes">
                {recoveryCodes.map((recovery, i) => (
                  // `--i` is the stagger index the shared entrance reads. It is clamped
                  // to `--stagger-max` in the token, so ten codes cascade over six steps
                  // rather than ten and the last one is not visibly late.
                  <li key={recovery} style={{ ['--i' as string]: i }}>{recovery}</li>
                ))}
              </ul>
              <label className="jmt-acknowledge">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAcknowledged(e.target.checked)
                  }
                />
                <span>I have written these down somewhere I can reach without this computer.</span>
              </label>
            </>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={busy || (step === 'codes' && !acknowledged)}
          >
            {STEP_ACTION[step]}
          </Button>
        </form>
      </div>
    </main>
  );
}

const STEP_SUBTITLE: Record<Step, string> = {
  password: 'Sign in to the operator console.',
  enrol: 'One more step before this account can be used.',
  code: 'Enter the code from your authenticator.',
  codes: 'Your recovery codes. This is the only time they are shown.',
  spent: 'Signed in. One thing worth knowing before you continue.',
};

const STEP_ACTION: Record<Step, string> = {
  password: 'Continue',
  enrol: 'Verify and enrol',
  code: 'Sign in',
  codes: 'Continue to the console',
  spent: 'Continue to the console',
};
